/**
 * QrCodeRenderer — pure-JS QR encoder + SVG renderer.
 *
 * Wraps the `toqr` package (12KB, no deps, MIT). The encoder returns a
 * Uint8Array of size N*N where each byte is 0 (light) or 1 (dark).
 * This component flips that into an SVG with one rect per dark module,
 * scaled to fit the requested px size.
 *
 * Used for the DPC-enrollment QR display (P1.F.4 follow-up). The
 * payload is JSON in the exact shape Android's setup wizard expects
 * (PROVISIONING_DEVICE_ADMIN_COMPONENT_NAME + ADMIN_EXTRAS_BUNDLE).
 */
import { useMemo } from "react";
import { toQR } from "toqr";

interface Props {
  /** Arbitrary string — JSON, URL, etc. */
  readonly payload: string;
  /** Output square dimension in px. Default 256. */
  readonly sizePx?: number;
  /** White margin around the matrix in modules. Default 2. */
  readonly quietZone?: number;
}

export function QrCodeRenderer({ payload, sizePx = 256, quietZone = 2 }: Props): JSX.Element {
  const svg = useMemo(() => renderSvg(payload, sizePx, quietZone), [payload, sizePx, quietZone]);
  return (
    <div
      className="inline-block bg-white p-2 rounded-md"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

function renderSvg(payload: string, sizePx: number, quietZone: number): string {
  const matrix = toQR(payload);
  const dim = Math.round(Math.sqrt(matrix.length));
  if (dim * dim !== matrix.length) {
    // Fallback — should never happen for valid QR output.
    return `<svg width="${sizePx}" height="${sizePx}" xmlns="http://www.w3.org/2000/svg">
      <rect width="100%" height="100%" fill="#fee2e2"/>
      <text x="50%" y="50%" font-size="14" text-anchor="middle" fill="#7f1d1d">QR encode failed</text>
    </svg>`;
  }
  const totalModules = dim + quietZone * 2;
  const moduleSize = sizePx / totalModules;
  const rects: string[] = [];
  for (let y = 0; y < dim; y += 1) {
    for (let x = 0; x < dim; x += 1) {
      if (matrix[y * dim + x] === 1) {
        const px = (x + quietZone) * moduleSize;
        const py = (y + quietZone) * moduleSize;
        rects.push(
          `<rect x="${px.toFixed(2)}" y="${py.toFixed(2)}" width="${moduleSize.toFixed(
            2,
          )}" height="${moduleSize.toFixed(2)}" fill="#000"/>`,
        );
      }
    }
  }
  return `<svg width="${sizePx}" height="${sizePx}" viewBox="0 0 ${sizePx} ${sizePx}" xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges">
    <rect width="100%" height="100%" fill="#fff"/>
    ${rects.join("")}
  </svg>`;
}
